"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  BookOpenText,
  Bookmark,
  CalendarDays,
  FileText,
  GraduationCap,
  LayoutDashboard,
  Megaphone,
  MessageSquare,
  Mic,
  Newspaper,
  PlaySquare,
  Users,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import type { ContentAvailability } from "./loadAvailability";
import type { PortalModules } from "@/lib/lecturehub/portal-config";

type Item = {
  label: string;
  href: string;
  Icon: LucideIcon;
  badge?: number;
  /** Full-content prefetch (Link default for dynamic routes only fetches up
   *  to the loading boundary). Set on the hot paths — dashboard + browse —
   *  so the FIRST click is instant, not just repeat visits. */
  prefetch?: boolean;
};

type Group = {
  title?: string;
  items: Item[];
};

export function AppLeftNav({
  lang,
  availability,
  modules,
}: {
  lang: Lang;
  availability: ContentAvailability;
  /** Tenant module-type toggles (`portal.moduleSettings`). A browse entry
      needs both: content exists (availability) AND the tenant has the type
      enabled. The assistant entry is gated on `aiChat` alone. */
  modules: PortalModules;
}) {
  const t = useDict();
  const currentPath = usePathname() ?? `/${lang}`;

  // Sections are named by INTENT, not "browse" (2026-08-25, after the A2go
  // trial). The order is the product owner's (2026-09-17): GLAVNO, then
  // NOVICE = what is new since you last looked, then AKADEMIJA =
  // everything you learn from or look up (courses = didactic chains, articles
  // = written knowledge, videos/podcasts/events, and the reference documents),
  // then the people directory. Module-type entries need both content
  // (availability) AND the tenant toggle; Documents/Posts gate on
  // toggle/presence alone but still carry a count badge.
  // Show an entry when content exists OR availability is unknown (the count
  // query failed) — fail OPEN so a transient error never hides navigation
  // (B16). null = unknown → show, no badge; 0 = known-empty → hide; >0 → badge.
  const hasOrUnknown = (n: number | null) => n == null || n > 0;

  const academy: Item[] = [];
  if (modules.course && hasOrUnknown(availability.course)) {
    academy.push({
      label: t.nav.courses,
      href: langPath(lang, "/library/courses"),
      Icon: BookOpenText,
      badge: availability.course ?? undefined,
      prefetch: true,
    });
  }
  // Articles sit directly after courses — the two strands of the Academy:
  // a course is a guided path, an article is knowledge you read. Editorial
  // content gated on the tenant toggle alone; the in-app /library/posts twin
  // (the public /posts stays for guests/SEO). Badge is the published-post count.
  if (modules.post) {
    academy.push({
      label: t.nav.posts,
      href: langPath(lang, "/library/posts"),
      Icon: Newspaper,
      badge: availability.post,
    });
  }
  if (modules.ott && hasOrUnknown(availability.ott)) {
    academy.push({
      label: t.nav.otts,
      href: langPath(lang, "/library/otts"),
      Icon: PlaySquare,
      badge: availability.ott ?? undefined,
      prefetch: true,
    });
  }
  if (modules.podcast && hasOrUnknown(availability.podcast)) {
    academy.push({
      label: t.nav.podcasts,
      href: langPath(lang, "/library/podcasts"),
      Icon: Mic,
      badge: availability.podcast ?? undefined,
      prefetch: true,
    });
  }
  if (modules.event && hasOrUnknown(availability.event)) {
    academy.push({
      label: t.nav.events,
      href: langPath(lang, "/library/events"),
      Icon: CalendarDays,
      badge: availability.event ?? undefined,
      prefetch: true,
    });
  }

  // Documents close the Academy (2026-09-17; they used to share a GRADIVA
  // section with the directory): a policy PDF is something you learn from or
  // look up, the same errand as an article. No backend portal serves a
  // "document" toggle row today (verified 2026-08-25), so the entry renders
  // everywhere via the fail-open rule — but the gate honors the toggle the day
  // the backend adds it.
  if (modules.document) {
    academy.push({
      label: t.nav.documents,
      href: langPath(lang, "/library/documents"),
      Icon: FileText,
      badge: availability.document,
    });
  }

  const groups: Group[] = [
    {
      // No title over the first group (2026-09-18): the desk itself — the
      // dashboard, the assistant, my academy, the library — needs no heading.
      items: [
        {
          label: t.appNav.dashboard,
          href: langPath(lang, "/dashboard"),
          Icon: LayoutDashboard,
          prefetch: true,
        },
        ...(modules.aiChat
          ? [
              {
                label: t.appNav.assistant,
                href: langPath(lang, "/asistent"),
                Icon: MessageSquare,
              },
            ]
          : []),
        {
          label: t.appNav.enrolled,
          href: langPath(lang, "/enrolled"),
          Icon: GraduationCap,
          prefetch: true,
        },
        {
          label: t.appNav.myLibrary,
          href: langPath(lang, "/saved"),
          Icon: Bookmark,
        },
      ],
    },
    {
      // Announcements are their own intent, deliberately NOT under Academy —
      // company news is not education — and second in the rail: after your own
      // desk, what changed. No tenant toggle; badge = published count (hidden
      // by NavItem when 0). The section is "Novice", the item "Vse novice".
      title: t.nav.news,
      items: [
        {
          label: t.dashboard.allNews,
          href: langPath(lang, "/library/news"),
          Icon: Megaphone,
          badge: availability.news,
        },
      ],
    },
    ...(academy.length > 0
      ? [
          {
            title: t.appNav.academy,
            items: academy,
          },
        ]
      : []),
    // The directory has a REAL tenant toggle, unlike documents — every live
    // portal carries the `directory` row — so this is gated for real rather
    // than fail-open. Its own section, LJUDJE, over the one item, SODELAVCI:
    // it never inherited the document toggle, and a portal with documents off
    // must not lose the directory with them. No badge: availability counts
    // describe catalogue content, and a staff list is not catalogue content.
    ...(modules.directory
      ? [
          {
            title: t.appNav.people,
            items: [
              {
                label: t.directory.title,
                href: langPath(lang, "/people"),
                Icon: Users,
              },
            ],
          },
        ]
      : []),
    // NB: no ACCOUNT section. Profile, subscriptions and password live in the
    // top-bar account menu (2026-08-25) — the rail is navigation, the avatar is
    // the account surface, per the Stripe / Shopify / GitHub arrangement.
    // Carrying both meant two entry points to /profil in one viewport.
  ];

  return (
    // Nothing above GLAVNO. A static "Začetek / Dobrodošli" card sat here until
    // 2026-09-17: no link, no action, no condition — ~100 px (two and a half
    // links) of the rail's first slot, on every page, for every member, repeating
    // the greeting the dashboard already gives by name. If this slot is ever
    // used again it should carry something that changes per person and leads
    // somewhere, not a welcome.
    <nav aria-label={t.appNav.appNavigation} className="flex-1 flex flex-col gap-1 py-4">
      {(() => {
        const allHrefs = groups.flatMap((g) => g.items.map((it) => it.href));
        return groups.map((g, gi) => (
          <section key={gi} className="mt-2">
            {g.title ? (
              <p
                className={cn(
                  "px-5 pt-3 pb-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-app-nav-foreground/40",
                  "group-data-[collapsed=1]:hidden",
                )}
              >
                {g.title}
              </p>
            ) : null}
            <ul className="px-2 flex flex-col gap-0.5">
              {g.items.map((it) => (
                <li key={it.href}>
                  <NavItem
                    label={it.label}
                    href={it.href}
                    Icon={it.Icon}
                    badge={it.badge}
                    prefetch={it.prefetch}
                    active={isActive(currentPath, it.href, allHrefs)}
                  />
                </li>
              ))}
            </ul>
          </section>
        ));
      })()}
    </nav>
  );
}

function pathDepth(path: string): number {
  return path.split("/").filter(Boolean).length;
}

function isActive(currentPath: string, href: string, allHrefs: string[]): boolean {
  // split() always returns at least one element on a string, so the ?? is just
  // for the type checker.
  const hrefPath = href.split("#")[0] ?? href;

  if (href.includes("#")) {
    const hasBareSibling = allHrefs.some((h) => h.split("#")[0] === hrefPath && !h.includes("#"));
    if (hasBareSibling) return false;
  }

  if (currentPath === hrefPath) return true;
  if (!currentPath.startsWith(hrefPath + "/")) return false;

  // Prefer items deeper in the URL tree — count segments, not string length,
  // so a long top-level slug never out-ranks a real sub-route.
  const ownDepth = pathDepth(hrefPath);
  const deeperMatch = allHrefs.some((h) => {
    const hp = h.split("#")[0] ?? h;
    if (pathDepth(hp) <= ownDepth) return false;
    return currentPath === hp || currentPath.startsWith(hp + "/");
  });
  return !deeperMatch;
}

function NavItem({
  label,
  href,
  Icon,
  active,
  badge,
  prefetch,
}: {
  label: string;
  href: string;
  Icon: LucideIcon;
  active: boolean;
  badge?: number;
  prefetch?: boolean;
}) {
  return (
    <Link
      href={href}
      prefetch={prefetch}
      aria-current={active ? "page" : undefined}
      title={label}
      className={cn(
        "group/item relative flex items-center gap-3 h-10 px-3 rounded-xl text-[14px] font-display font-bold",
        "transition-[background-color,color,transform,box-shadow] duration-150",
        "group-data-[collapsed=1]:justify-center group-data-[collapsed=1]:px-0 group-data-[collapsed=1]:mx-2",
        "active:scale-[0.98]",
        active
          ? "bg-app-nav-foreground/10 text-app-nav-foreground ring-1 ring-app-nav-foreground/10"
          : "text-app-nav-foreground/80 hover:bg-app-nav-foreground/[0.06] hover:text-app-nav-foreground",
      )}
    >
      {active ? (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-app-nav-icon group-data-[collapsed=1]:hidden"
        />
      ) : null}
      <Icon
        className="size-5 shrink-0 text-app-nav-icon"
        strokeWidth={active ? 2.25 : 2}
        aria-hidden
      />
      <span className="truncate flex-1 group-data-[collapsed=1]:hidden">{label}</span>
      {badge != null && badge > 0 ? (
        <span
          className={cn(
            "shrink-0 text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full",
            "group-data-[collapsed=1]:hidden",
            active
              ? "bg-app-nav-foreground/15 text-app-nav-foreground"
              : "bg-app-nav-foreground/10 text-app-nav-foreground/70",
          )}
          aria-hidden
        >
          {badge}
        </span>
      ) : null}
    </Link>
  );
}
